/**
 * syc-card-builder.js
 *
 * JS counterpart to snippets/syc-product-card.liquid.
 * Used wherever product cards must be built client-side (e.g. the wishlist
 * carousel, which is fully JS-rendered from a PHP API response).
 *
 * The Liquid snippet remains the single source of truth for server-rendered
 * contexts. This file mirrors its markup and logic so both stay in sync.
 *
 * Setup (in a Liquid section before this script loads):
 *   <script>
 *     window.SycCardBuilder.configure({
 *       moneyFormat:  {{ shop.money_format | json }},
 *       discountCode: {{ settings.active_discount_code | default: '' | json }},
 *       discountPct:  {{ settings.active_discount_percent | default: 0 | json }}
 *     });
 *   </script>
 */

(function() {
    'use strict';

    var _cfg = {
        moneyFormat: '${{amount}}',
        discountCode: '',
        discountPct: 0
    };

    // ── Mirrors product.price | divided_by: 100 | money ───────────────────
    // storeCents = rawCents / 100  (caller applies the store-specific correction)
    function formatMoney(storeCents) {
        if (storeCents == null || storeCents === '') return '';
        var cents = Math.round(Number(storeCents));
        if (!isFinite(cents)) return '';
        var fmt = _cfg.moneyFormat;
        var dollars = cents / 100;
        var value;
        if (fmt.indexOf('amount_no_decimals_with_comma_separators') !== -1) {
            value = Math.floor(dollars).toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        } else if (fmt.indexOf('amount_with_comma_separators') !== -1) {
            value = dollars.toFixed(2).replace('.', ',')
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        } else if (fmt.indexOf('amount_no_decimals') !== -1) {
            value = Math.floor(dollars).toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        } else {
            value = dollars.toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
    }

    function esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;')
            .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    // Mirrors Liquid's `capitalize` filter (first letter up, rest down)
    // applied per word — same as catalog-card.liquid's Title Case treatment
    // of catalog_type, so both cards format it identically regardless of
    // how the merchant typed it in admin.
    function titleCase(s) {
        return String(s || '').split(' ').filter(Boolean).map(function (w) {
            return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
        }).join(' ');
    }

    // ── Build a syc-card <a> element (mirrors syc-product-card.liquid) ────
    // p        — Shopify product object from /products/<handle>.js
    // opts     — {
    //     id            : product id string (for data attrs / remove buttons)
    //     removeButton  : boolean — add the wishlist-page X remove button
    //     roleListitem  : boolean — add role="listitem" to the <a>
    //   }
    function buildCard(p, opts) {
        opts = opts || {};
        var id = String(opts.id != null ? opts.id : (p.id || ''));

        var TITLE_MAX = 75;
        var title = p.title || 'Product';
        if (title.length > TITLE_MAX) title = title.slice(0, TITLE_MAX - 1) + '…';

        // opts.brand is injected by Wishlist.liquid from the localStorage cache
        // (populated by wishlist-button.liquid at render time from the Liquid metafield).
        var brand = (opts.brand !== undefined && opts.brand !== null && opts.brand !== '')
          ? String(opts.brand)
          : ((p.metafields && p.metafields.custom && p.metafields.custom.product_brand) || '');

        // Images
        var featured = p.featured_image || (p.images && p.images[0]) || '';
        var secondary = (p.images && p.images[1]) || '';
        if (featured && featured.indexOf && featured.indexOf('//') === 0) featured = 'https:' + featured;
        if (secondary && secondary.indexOf && secondary.indexOf('//') === 0) secondary = 'https:' + secondary;

        // Pass raw Shopify cents directly — mirrors: product.price | money
        // formatMoney handles the /100 conversion internally.
        var p_price = Number(p.price);
        var p_compare = Number(p.compare_at_price);
        var priceStr = formatMoney(p_price);
        var compareStr = p.compare_at_price > p.price ? formatMoney(p_compare) : '';

        // Logged-in customers see the real price during the preview window,
        // matching main-product.liquid / syc-product-card.liquid - only a
        // logged-out visitor gets the "Price revealed in N days" placeholder.
        var isLoggedIn = !!(window.SYC_WISHLIST_CUSTOMER && window.SYC_WISHLIST_CUSTOMER.id != null);
        var catalogLiveTs = Number(opts.catalogLiveTs) || 0;
        var catalogUpcoming = catalogLiveTs > 0 && Math.floor(Date.now() / 1000) < catalogLiveTs && !isLoggedIn;

        var discountActive = !!(_cfg.discountCode && _cfg.discountPct > 0);
        var priceHtml = '', badgeHtml = '';

        if (!catalogUpcoming) {
            if (compareStr) {
                priceHtml =
                    '<span class="syc-card__price--compare">' + esc(compareStr) + '</span>' +
                    '<span class="syc-card__price--sale">' + esc(priceStr) + '</span>';
            } else if (discountActive) {
                var discounted = p_price * (100 - _cfg.discountPct) / 100;
                priceHtml =
                    '<span class="syc-card__price--compare">' + esc(priceStr) + '</span>' +
                    '<span class="syc-card__price--sale">' + esc(formatMoney(discounted)) + '</span>';
            } else {
                priceHtml = '<span>' + esc(priceStr) + '</span>';
            }
            if (discountActive) {
                badgeHtml = '<p class="syc-card__discount-badge">Discount Applied: ' + esc(_cfg.discountCode) + '</p>';
            }
        }

        var hasSecondary = !!secondary;
        var mediaCls = 'syc-card__media' + (hasSecondary ? ' syc-card__media--has-secondary' : '');

        var imgPrimary = featured ?
            '<img class="syc-card__img syc-card__img--primary" src="' + esc(featured) + '" alt="' + esc(p.title || '') + '" loading="lazy">' :
            '';
        var imgSecondary = hasSecondary ?
            '<img class="syc-card__img syc-card__img--secondary" src="' + esc(secondary) + '" alt="" loading="lazy" aria-hidden="true">' :
            '';

        var brandHtml = brand ?
            '<p class="syc-card__brand">' + esc(brand) + '</p>' :
            '';

        var removeBtn = opts.removeButton ?
            '<button type="button" class="wl-remove" data-remove-product="' + esc(id) + '" aria-label="Remove from wishlist">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4l16 16M20 4L4 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
            '</button>' :
            '';

        // Add-to-wishlist heart — mirrors snippets/wishlist-button.liquid's
        // display_style: 'icon' markup exactly (same classes/data-* attrs),
        // so window.initWishlistButtons() (assets/wishlist.js) wires it up
        // identically to a server-rendered one. Only for the regular browse
        // context: the Wishlist page itself uses removeBtn above instead
        // (you're already in your wishlist there, so "remove" is the
        // relevant action, not "add"). The shared <style> block that styles
        // .wishlist-icon/.wishlist-btn lives in wishlist-button.liquid and is
        // already on the page via main-collection-banner.liquid, so it isn't
        // duplicated here — this only needs to emit matching markup.
        var wishlistHtml = '';
        if (!opts.removeButton) {
            var priceNoCurrency = isFinite(p_price) ? (p_price / 100).toFixed(2) : '';
            wishlistHtml =
                '<div class="syc-card__wishlist-wrap" onclick="event.stopPropagation()">' +
                '<div class="wishlist-icon" data-wishlist-root>' +
                '<button class="wishlist-btn" data-style="icon" aria-label="Add to Wishlist" ' +
                'data-label-inactive="Add to Wishlist" data-label-active="In Wishlist" data-mode="product" ' +
                'data-id="' + esc(id) + '" data-title="' + esc(p.title || '') + '" data-handle="' + esc(p.handle) + '" ' +
                'data-brand="' + esc(brand) + '" data-image="' + esc(featured) + '" data-price="' + esc(priceNoCurrency) + '" ' +
                'data-category="" data-catalog-live-ts="' + catalogLiveTs + '" data-catalog-id="' + esc(opts.catalogId || 0) + '" data-catalog-type="" ' +
                'data-product-id="' + esc(id) + '" data-product-title="' + esc(p.title || '') + '" data-product-handle="' + esc(p.handle) + '">' +
                '<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><defs></defs>' +
                '<path class="c" d="m43,17.0766c0-5.6539-4.5835-10.2373-10.2374-10.2373-3.7223,0-6.9708,1.9932-8.7626,4.964-1.7919-2.9708-5.0403-4.964-8.7626-4.964-5.6539,0-10.2373,4.5834-10.2373,10.2373,0,1.2925.2496,2.524.6866,3.6627,3.3851,9.7368,18.3134,20.4215,18.3134,20.4215,0,0,14.9282-10.6847,18.3134-20.4215.437-1.1386.6867-2.3702.6867-3.6627Z"/>' +
                '</svg>' +
                '<div class="wishlist-count" data-product-id="' + esc(id) + '"><span class="count-number"></span></div>' +
                '</button>' +
                '</div>' +
                '</div>';
        }

        // opts.trending — opt-in only (e.g. sections/trending-products.liquid),
        // not part of buildCard()'s default output, since this same function
        // is also used by the Wishlist page and Shop All's accurate view,
        // where a trending badge wouldn't make sense. Matches
        // sections/live-auctions.liquid's / snippets/catalog-card.liquid's
        // seuyco-lot__trending exactly (same markup + colors) so all three
        // read as one consistent visual language.
        var trendingHtml = opts.trending
            ? '<span class="seuyco-lot__trending" role="img" aria-label="Trending: one of the most-viewed products right now" title="Trending: one of the most-viewed products right now">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
              '<polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>' +
              '</svg>' +
              '</span>'
            : '';

        var aAttrs = 'class="syc-card" href="/products/' + esc(p.handle) + '"' +
            (opts.roleListitem ? ' role="listitem"' : '') +
            (id ? ' data-product-id="' + esc(id) + '" data-track-product-id="' + esc(id) + '"' : '') +
            (opts.catalogId ? ' data-track-catalog-id="' + esc(opts.catalogId) + '"' : '');

        var priceSection;
        if (catalogUpcoming) {
            var secsLeft = catalogLiveTs - Math.floor(Date.now() / 1000);
            var daysLeft = Math.max(0, Math.ceil(secsLeft / 86400));
            var countdownMsg = daysLeft === 0 ? 'Price revealed today'
                : daysLeft === 1 ? 'Price revealed tomorrow'
                : 'Price revealed in ' + daysLeft + ' days';
            priceSection = '<p class="syc-card__discount-badge">' + esc(countdownMsg) + '</p>';
        } else {
            priceSection = '<p class="syc-card__price">' + priceHtml + '</p>' + badgeHtml;
        }

        return '<a ' + aAttrs + '>' +
            removeBtn +
            '<div class="' + mediaCls + '">' + imgPrimary + imgSecondary + wishlistHtml + trendingHtml + '</div>' +
            '<div class="syc-card__content">' +
            brandHtml +
            '<p class="syc-card__title">' + esc(title) + '</p>' +
            priceSection +
            '</div>' +
            '</a>';
    }

    // ── Collection card — mirrors snippets/catalog-card.liquid's new
    // auction-card-style layout (status header, top-3 product thumbnails,
    // dark CTA bar). Used by the wishlist collections carousel.
    //
    // c.topProducts: array of up to 3 product objects (from the same
    // /collections/<handle>/products.json response already fetched to get
    // productCount — see hydrateCollection() in sections/Wishlist.liquid).
    // "Top 3" here is just collection order, same placeholder as the Liquid
    // snippet, until the Google-Analytics-driven view metric lands.
    function buildCollectionCard(id, c) {
        var now = Math.floor(Date.now() / 1000);
        var openTs  = Number(c.catalogLiveTs) || 0;
        var closeTs = Number(c.closeTs) || 0;

        var status = '';
        if (openTs > 0 && closeTs > 0 && now < closeTs) {
            status = now >= openTs ? 'live' : 'upcoming';
        }
        // Unlike the Liquid snippet (which only ever loops actual catalog
        // collections), a saved wishlist entry can be any collection,
        // including ones with no catalog metafields at all or an expired
        // window — keep showing those, just without a status badge/date,
        // rather than silently dropping them from someone's wishlist.

        function fmtDate(ts) {
            var d = new Date(ts * 1000);
            return (d.getMonth() + 1) + '/' + d.getDate();
        }

        var statusHtml = '';
        if (status) {
            var badgeLabel = status === 'live' ? 'OPEN' : 'UPCOMING';
            var dateText = status === 'live' ? 'Closing ' + fmtDate(closeTs) : 'Opens ' + fmtDate(openTs);
            statusHtml = '<span class="catalog-card__status">' +
                '<span class="catalog-card__badge catalog-card__badge--' + status + '">' + badgeLabel + '</span>' +
                '<span class="catalog-card__date">' + esc(dateText) + '</span>' +
                '</span>';
        }
        var typeHtml = c.catalogType
            ? '<span class="catalog-card__type">' + esc(titleCase(c.catalogType)) + '</span>'
            : '';

        var countHtml = c.productCount > 0
            ? '<span class="catalog-card__count">' + esc(String(c.productCount)) + ' products</span>'
            : '';

        var metaHtml = (typeHtml || countHtml)
            ? '<div class="catalog-card__meta">' + typeHtml + countHtml + '</div>'
            : '';

        var lotsHtml = (c.topProducts || []).slice(0, 3).map(function (p) {
            var img = p.featured_image || (p.images && p.images[0]) || '';
            if (img && img.src) img = img.src;
            if (typeof img === 'string' && img.indexOf('//') === 0) img = 'https:' + img;
            var imgHtml = img
                ? '<img class="catalog-card__lot-img" src="' + esc(img) + '" alt="' + esc(p.title || '') + '" loading="lazy">'
                : '<div class="catalog-card__lot-img catalog-card__lot-img--placeholder" aria-hidden="true"></div>';
            return '<a class="catalog-card__lot" href="/products/' + esc(p.handle) + '" data-track-product-id="' + esc(p.id) + '" data-track-catalog-id="' + esc(id) + '">' + imgHtml + '</a>';
        }).join('');

        var removeBtn = '<button type="button" class="wl-remove" data-remove-collection="' + esc(id) + '" aria-label="Remove from wishlist">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4l16 16M20 4L4 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
            '</button>';

        var ctaLabel = status === 'live' ? 'Shop Catalog' : status === 'upcoming' ? 'Preview Catalog' : 'View Catalog';

        return '<div class="catalog-card" role="listitem" data-collection-id="' + esc(id) + '">' +
            '<div class="catalog-card__head">' + statusHtml + removeBtn + '</div>' +
            '<h3 class="catalog-card__name"><a href="/collections/' + esc(c.handle) + '">' + esc(c.title) + '</a></h3>' +
            metaHtml +
            '<div class="catalog-card__lots">' + lotsHtml + '</div>' +
            '<a href="/collections/' + esc(c.handle) + '" class="catalog-card__cta">' +
            '<span>' + ctaLabel + '</span><span>&rarr;</span>' +
            '</a>' +
            '</div>';
    }

    window.SycCardBuilder = {
        configure: function(cfg) {
            if (!cfg) return;
            if (cfg.moneyFormat != null) _cfg.moneyFormat = cfg.moneyFormat;
            if (cfg.discountCode != null) _cfg.discountCode = cfg.discountCode;
            if (cfg.discountPct != null) _cfg.discountPct = Number(cfg.discountPct);
        },
        formatMoney: formatMoney,
        buildCard: buildCard,
        buildCollectionCard: buildCollectionCard
    };
})();